import {
  type BigDecimal,
  createBigDecimal,
  divide,
  equals,
  greaterThan,
  minus,
  plus,
  times,
} from '@0xtorch/big-decimal'
import type { AssetBalance } from '../types/assetBalance'
import type { AssetBorrow } from '../types/assetBorrow'
import type { Transaction, TransactionTransfer } from '../types/transaction'
import { createAssetKey } from '../utils'
import { getTotalPl, getTotalTradePl } from './utils/transactionTransferPl'

type CalculateTransactionPlByWacbParameters = {
  transaction: Transaction
  assetBalances: Map<string, BigDecimal>
  assetBorrows: Map<string, readonly AssetBorrow[]>
  totalBalances: Map<string, AssetBalance>
}

type CalculateTransactionPlByWacbReturnTypes = {
  transaction: Transaction
  assetBalances: Map<string, BigDecimal>
  assetBorrows: Map<string, readonly AssetBorrow[]>
  totalBalances: Map<string, AssetBalance>
}

export const calculateTransactionPlByWacb = (
  params: CalculateTransactionPlByWacbParameters,
): CalculateTransactionPlByWacbReturnTypes => {
  const { transaction, assetBalances, assetBorrows, totalBalances } = params

  switch (transaction.category) {
    case 'blank': {
      return params
    }
    case 'borrow': {
      let newAssetBorrows = assetBorrows
      for (const transfer of transaction.ins) {
        newAssetBorrows = addAssetBorrow({
          transfer,
          assetBorrows: newAssetBorrows,
        })
      }
      return {
        ...params,
        assetBorrows: newAssetBorrows,
      }
    }
    case 'deposit': {
      // TODO deposit を処理できるようにする
      return params
    }
    case 'fee': {
      let newAssetBalances = assetBalances
      let newAssetBorrows = assetBorrows
      let newTotalBalances = totalBalances
      for (const transfer of transaction.outs) {
        const result = getCost({
          transfer,
          assetBalances: newAssetBalances,
          assetBorrows: newAssetBorrows,
          totalBalances: newTotalBalances,
        })
        newAssetBalances = result.assetBalances
        newAssetBorrows = result.assetBorrows
        newTotalBalances = result.totalBalances
        const costDiff = minus(result.cost, transfer.cost)
        transfer.cost = result.cost
        applyTradePlByHoldingPeriod({
          transfer,
          tradePl: minus(getTotalTradePl({ transfer }), costDiff),
        })
      }
      return {
        transaction,
        assetBalances: newAssetBalances,
        assetBorrows: newAssetBorrows,
        totalBalances: newTotalBalances,
      }
    }
    case 'income': {
      let newAssetBalances = assetBalances
      let newAssetBorrows = assetBorrows
      for (const transfer of transaction.ins) {
        const result = addAssetBalance({
          transfer,
          assetBalances: newAssetBalances,
          assetBorrows: newAssetBorrows,
        })
        newAssetBalances = result.assetBalances
        newAssetBorrows = result.assetBorrows
      }
      return {
        ...params,
        assetBalances: newAssetBalances,
      }
    }
    case 'insufficient': {
      let newAssetBalances = assetBalances
      let newAssetBorrows = assetBorrows
      for (const transfer of transaction.ins) {
        const result = addAssetBalance({
          transfer,
          assetBalances: newAssetBalances,
          assetBorrows: newAssetBorrows,
        })
        newAssetBalances = result.assetBalances
        newAssetBorrows = result.assetBorrows
      }
      return {
        ...params,
        assetBalances: newAssetBalances,
      }
    }
    case 'repayment': {
      let newAssetBorrows = assetBorrows
      for (const transfer of transaction.outs) {
        newAssetBorrows = removeAssetBorrow({
          transfer,
          assetBorrows: newAssetBorrows,
        })
      }
      return {
        ...params,
        assetBorrows: newAssetBorrows,
      }
    }
    case 'replace': {
      let newAssetBalances = assetBalances
      let newAssetBorrows = assetBorrows
      let newTotalBalances = totalBalances
      for (const transfer of transaction.ins) {
        const result = addAssetBalance({
          transfer,
          assetBalances: newAssetBalances,
          assetBorrows: newAssetBorrows,
        })
        newAssetBalances = result.assetBalances
        newAssetBorrows = result.assetBorrows
      }
      for (const transfer of transaction.outs) {
        const result = getCost({
          transfer,
          assetBalances: newAssetBalances,
          assetBorrows: newAssetBorrows,
          totalBalances: newTotalBalances,
        })
        newAssetBalances = result.assetBalances
        newAssetBorrows = result.assetBorrows
        newTotalBalances = result.totalBalances
      }
      return {
        transaction,
        assetBalances: newAssetBalances,
        assetBorrows: newAssetBorrows,
        totalBalances: newTotalBalances,
      }
    }
    case 'trade': {
      let newAssetBalances = assetBalances
      let newAssetBorrows = assetBorrows
      let newTotalBalances = totalBalances
      for (const transfer of transaction.ins) {
        const result = addAssetBalance({
          transfer,
          assetBalances: newAssetBalances,
          assetBorrows: newAssetBorrows,
        })
        newAssetBalances = result.assetBalances
        newAssetBorrows = result.assetBorrows
      }
      for (const transfer of transaction.outs) {
        const result = getCost({
          transfer,
          assetBalances: newAssetBalances,
          assetBorrows: newAssetBorrows,
          totalBalances: newTotalBalances,
        })
        newAssetBalances = result.assetBalances
        newAssetBorrows = result.assetBorrows
        newTotalBalances = result.totalBalances
        const costDiff = minus(result.cost, transfer.cost)
        transfer.cost = result.cost
        applyTradePlByHoldingPeriod({
          transfer,
          tradePl: minus(getTotalTradePl({ transfer }), costDiff),
        })
      }
      return {
        transaction,
        assetBalances: newAssetBalances,
        assetBorrows: newAssetBorrows,
        totalBalances: newTotalBalances,
      }
    }
    case 'transfer': {
      let newAssetBalances = assetBalances
      let newAssetBorrows = assetBorrows
      let newTotalBalances = totalBalances
      for (const transfer of transaction.outs) {
        const result = getCost({
          transfer,
          assetBalances: newAssetBalances,
          assetBorrows: newAssetBorrows,
          totalBalances: newTotalBalances,
        })
        newAssetBalances = result.assetBalances
        newAssetBorrows = result.assetBorrows
        newTotalBalances = result.totalBalances
        const costDiff = minus(result.cost, transfer.cost)
        transfer.cost = result.cost
        applyTradePlByHoldingPeriod({
          transfer,
          tradePl: minus(getTotalTradePl({ transfer }), costDiff),
        })
      }
      return {
        transaction,
        assetBalances: newAssetBalances,
        assetBorrows: newAssetBorrows,
        totalBalances: newTotalBalances,
      }
    }
    case 'value-down': {
      let newAssetBalances = assetBalances
      let newAssetBorrows = assetBorrows
      for (const transfer of transaction.ins) {
        const result = addAssetBalance({
          transfer,
          assetBalances: newAssetBalances,
          assetBorrows: newAssetBorrows,
        })
        newAssetBalances = result.assetBalances
        newAssetBorrows = result.assetBorrows
      }
      return {
        ...params,
        assetBalances: newAssetBalances,
      }
    }
    case 'value-up': {
      let newAssetBalances = assetBalances
      let newAssetBorrows = assetBorrows
      let newTotalBalances = totalBalances
      for (const transfer of transaction.outs) {
        const result = getCost({
          transfer,
          assetBalances: newAssetBalances,
          assetBorrows: newAssetBorrows,
          totalBalances: newTotalBalances,
        })
        newAssetBalances = result.assetBalances
        newAssetBorrows = result.assetBorrows
        newTotalBalances = result.totalBalances
        const costDiff = minus(result.cost, transfer.cost)
        transfer.cost = result.cost
        applyTradePlByHoldingPeriod({
          transfer,
          tradePl: minus(getTotalTradePl({ transfer }), costDiff),
        })
      }
      return {
        transaction,
        assetBalances: newAssetBalances,
        assetBorrows: newAssetBorrows,
        totalBalances: newTotalBalances,
      }
    }
    case 'withdraw': {
      // TODO withdraw を処理できるようにする
      return params
    }
    default: {
      throw new Error(
        `Unknown transaction category: ${transaction.category satisfies never}`,
      )
    }
  }
}

/**
 * 再計算後の trade PL を NFT 保有期間ルールに従って transfer へ反映します
 * @param transfer 対象 transfer
 * @param tradePl 再計算後の trade PL
 */
const applyTradePlByHoldingPeriod = ({
  transfer,
  tradePl,
}: {
  readonly transfer: TransactionTransfer
  readonly tradePl: BigDecimal
}): void => {
  const currentNftCapitalPl = transfer.nftCapitalPl ?? createBigDecimal(0n)

  if (transfer.asset.type !== 'Nft') {
    transfer.tradePl = tradePl
    transfer.nftTradePl = createBigDecimal(0n)
    transfer.nftCapitalPl = createBigDecimal(0n)
  } else if (currentNftCapitalPl.value !== 0n) {
    transfer.tradePl = createBigDecimal(0n)
    transfer.nftTradePl = createBigDecimal(0n)
    transfer.nftCapitalPl = tradePl
  } else {
    transfer.tradePl = createBigDecimal(0n)
    transfer.nftTradePl = tradePl
    transfer.nftCapitalPl = createBigDecimal(0n)
  }

  transfer.separateTradePl = transfer.separateTradePl ?? createBigDecimal(0n)
  transfer.separateRealizedPl =
    transfer.separateRealizedPl ?? createBigDecimal(0n)
  transfer.realizedPl = transfer.realizedPl ?? createBigDecimal(0n)
  transfer.airdropPl = transfer.airdropPl ?? createBigDecimal(0n)
  transfer.totalPl = getTotalPl({ transfer })
}

const addAssetBalance = (params: {
  transfer: TransactionTransfer
  assetBalances: Map<string, BigDecimal>
  assetBorrows: Map<string, readonly AssetBorrow[]>
}): {
  assetBalances: Map<string, BigDecimal>
  assetBorrows: Map<string, readonly AssetBorrow[]>
} => {
  const { transfer, assetBalances, assetBorrows } = params

  let remainAmount = transfer.amount
  let remainValue = times(transfer.amount, transfer.price, 6)

  // amount < borrowed amount の asset borrow の amount を borrowed amount まで増やす
  const key = createAssetKey(transfer.asset)
  const targetAssetBorrows = assetBorrows.get(key) ?? []
  for (const assetBorrow of targetAssetBorrows) {
    if (greaterThan(assetBorrow.borrowedAmount, assetBorrow.amount)) {
      const diff = minus(assetBorrow.borrowedAmount, assetBorrow.amount)

      // remainAmount <= diff の場合
      // - amount に remainAmount 追加
      // - value に remainValue 追加
      // - remainAmount, remainValue 0 にする
      // - 処理終了
      if (!greaterThan(remainAmount, diff)) {
        assetBorrow.amount = plus(assetBorrow.amount, remainAmount)
        assetBorrow.value = plus(assetBorrow.value, remainValue)
        remainAmount = createBigDecimal(0n)
        remainValue = createBigDecimal(0n)
        break
      }

      // remainAmount > diff の場合
      // - amount に diff 追加
      // - value に remainValue * diff / remainAmount 追加
      // - remainAmount から diff を引く
      // - remainValue から remainValue * diff / remainAmount を引く
      const valueToAdd = divide(times(remainValue, diff, 6), remainAmount, 6)
      assetBorrow.amount = plus(assetBorrow.amount, diff)
      assetBorrow.value = plus(assetBorrow.value, valueToAdd)
      remainAmount = minus(remainAmount, diff)
      remainValue = minus(remainValue, valueToAdd)
    }
  }
  assetBorrows.set(key, targetAssetBorrows)

  // remainAmount = 0 だったら asset balance には追加せず値を返す
  if (remainAmount.value === 0n) {
    return {
      assetBalances,
      assetBorrows,
    }
  }

  // const key = createAssetKey(transfer.asset)
  assetBalances.set(
    key,
    plus(assetBalances.get(key) ?? createBigDecimal(0n), remainAmount),
  )
  return {
    assetBalances,
    assetBorrows,
  }
}

const addAssetBorrow = (params: {
  transfer: TransactionTransfer
  assetBorrows: Map<string, readonly AssetBorrow[]>
}): Map<string, readonly AssetBorrow[]> => {
  const { transfer, assetBorrows } = params
  const key = createAssetKey(transfer.asset)
  const value = times(transfer.amount, transfer.price, 6)
  assetBorrows.set(key, [
    ...(assetBorrows.get(key) ?? []),
    {
      amount: transfer.amount,
      value,
      borrowedAmount: transfer.amount,
      borrowedValue: value,
    },
  ])
  return assetBorrows
}

const removeAssetBorrow = (params: {
  transfer: TransactionTransfer
  assetBorrows: Map<string, readonly AssetBorrow[]>
}): Map<string, readonly AssetBorrow[]> => {
  const { transfer, assetBorrows } = params
  const key = createAssetKey(transfer.asset)
  let remain = transfer.amount
  const targetAssetBorrows = assetBorrows.get(key) ?? []
  const newAssetBorrows: AssetBorrow[] = []
  for (const borrow of targetAssetBorrows) {
    // remain = 0 の場合
    // - newAssetBorrows に borrow data 追加
    if (remain.value === 0n) {
      newAssetBorrows.push(borrow)
      continue
    }

    // borrow.amount <= remain の場合
    // - remain -= borrow.amount
    // borrowedAmount > amount の場合は borrowed data は比率分追加して、余りを newAssetBorrows に追加
    // - assetBorrow に borrow data 追加
    if (!greaterThan(borrow.amount, remain)) {
      remain = minus(remain, borrow.amount)
      if (greaterThan(borrow.borrowedAmount, borrow.amount)) {
        const borrowedValuePerAmount = divide(
          times(borrow.borrowedValue, borrow.amount, 6),
          borrow.borrowedAmount,
          6,
        )
        newAssetBorrows.push({
          amount: createBigDecimal(0n),
          value: createBigDecimal(0n),
          borrowedAmount: minus(borrow.borrowedAmount, borrow.amount),
          borrowedValue: minus(borrow.borrowedValue, borrowedValuePerAmount),
        })
        continue
      }
      continue
    }

    // borrow.amount > remain の場合
    // - remain = 0
    // - assetBorrow に remain 分の borrow data 追加
    // - borrow から remain 分の borrow data を引いた残りを newAssetBorrows に追加
    const valuePerRemain = divide(
      times(borrow.value, remain, 6),
      borrow.amount,
      6,
    )
    const borrowedValuePerRemain = divide(
      times(borrow.borrowedValue, remain, 6),
      borrow.borrowedAmount,
      6,
    )
    newAssetBorrows.push({
      amount: minus(borrow.amount, remain),
      value: minus(borrow.value, valuePerRemain),
      borrowedAmount: minus(borrow.borrowedAmount, remain),
      borrowedValue: minus(borrow.borrowedValue, borrowedValuePerRemain),
    })
    remain = createBigDecimal(0n)
  }
  if (remain.value > 0n) {
    throw new Error('Insufficient asset')
  }
  assetBorrows.set(key, newAssetBorrows)

  return assetBorrows
}

const getCost = (params: {
  transfer: TransactionTransfer
  assetBalances: Map<string, BigDecimal>
  assetBorrows: Map<string, readonly AssetBorrow[]>
  totalBalances: Map<string, AssetBalance>
}): {
  cost: BigDecimal
  assetBalances: Map<string, BigDecimal>
  assetBorrows: Map<string, readonly AssetBorrow[]>
  totalBalances: Map<string, AssetBalance>
} => {
  const { transfer, assetBalances, assetBorrows, totalBalances } = params

  if (transfer.asset.type === 'FiatCurrency') {
    return { ...params, cost: transfer.cost }
  }

  const key = createAssetKey(transfer.asset)

  // asset balance (total balance) から cost 取得 & asset balance, total balance 更新
  const {
    remain: remainByAssetBalance,
    cost: costByAssetBalance,
    assetBalance,
    totalBalance,
  } = getCostByAssetBalance({
    amount: transfer.amount,
    assetBalance: assetBalances.get(key) ?? createBigDecimal(0n),
    totalBalance: totalBalances.get(key) ?? {
      amount: createBigDecimal(0n),
      value: createBigDecimal(0n),
    },
  })
  assetBalances.set(key, assetBalance)
  totalBalances.set(key, totalBalance)

  // remain = 0 だったら asset borrow は使用せず値を返す
  if (remainByAssetBalance.value === 0n) {
    return {
      cost: costByAssetBalance,
      assetBalances,
      assetBorrows,
      totalBalances,
    }
  }

  // asset borrow から cost 取得
  let remain = remainByAssetBalance
  let cost = costByAssetBalance
  const keyAssetBorrows = assetBorrows.get(key) ?? []
  for (const assetBorrow of keyAssetBorrows) {
    // amount = remain の場合
    // - cost に value 追加
    // - amount, value, remain 0 にする
    // - 処理終了
    if (equals(assetBorrow.amount, remain)) {
      cost = plus(cost, assetBorrow.value)
      remain = createBigDecimal(0n)
      assetBorrow.amount = createBigDecimal(0n)
      assetBorrow.value = createBigDecimal(0n)
      break
    }

    // amount > remain の場合
    // - amount から remain 引く
    // - cost に remain 分の value 追加 & value からその分引く
    // - remain 0 にする
    // - 処理終了
    if (greaterThan(assetBorrow.amount, remain)) {
      const valuePerRemain = divide(
        times(assetBorrow.value, remain, 6),
        assetBorrow.amount,
        6,
      )
      assetBorrow.amount = minus(assetBorrow.amount, remain)
      assetBorrow.value = minus(assetBorrow.value, valuePerRemain)
      cost = plus(cost, valuePerRemain)
      remain = createBigDecimal(0n)
      break
    }

    // amount < remain の場合
    // - cost に　value 追加
    // - remain から amount 引く
    // - amount, value 0 にする
    cost = plus(cost, assetBorrow.value)
    remain = minus(remain, assetBorrow.amount)
    assetBorrow.amount = createBigDecimal(0n)
    assetBorrow.value = createBigDecimal(0n)
  }
  assetBorrows.set(key, keyAssetBorrows)

  if (remain.value > 0n) {
    throw new Error('Insufficient asset')
  }

  return {
    cost,
    assetBalances,
    assetBorrows,
    totalBalances,
  }

  // const assetBalance = assetBalances.get(key)
  // if (assetBalance === undefined) {
  //   throw new Error(`Asset balance not found: ${key}`)
  // }
  // if (greaterThan(transfer.amount, assetBalance.amount)) {
  //   throw new Error(`Insufficient asset balance: ${key}`)
  // }
  // const amountIsEqual = equals(transfer.amount, assetBalance.amount)
  // const cost = amountIsEqual
  //   ? assetBalance.value
  //   : divide(
  //       times(assetBalance.value, transfer.amount, 6),
  //       assetBalance.amount,
  //       6,
  //     )
  // if (amountIsEqual) {
  //   assetBalances.delete(key)
  // } else {
  //   assetBalances.set(key, {
  //     amount: minus(assetBalance.amount, transfer.amount),
  //     value: minus(assetBalance.value, cost),
  //   })
  // }
  // return { cost, assetBalances }
}

const getCostByAssetBalance = ({
  amount,
  assetBalance,
  totalBalance,
}: {
  readonly amount: BigDecimal
  readonly assetBalance: BigDecimal
  readonly totalBalance: AssetBalance
}): {
  readonly remain: BigDecimal
  readonly cost: BigDecimal
  readonly assetBalance: BigDecimal
  readonly totalBalance: AssetBalance
} => {
  // balance.amount = amount の場合
  // - cost = totalBalance.value * amount / totalBalance.amount
  // - remain = 0
  // - balance.amount, balance.value 0 にして、空配列返す
  // - used balance に balance 追加
  if (equals(assetBalance, amount)) {
    const cost = equals(totalBalance.amount, amount)
      ? totalBalance.value
      : divide(times(totalBalance.value, amount, 6), totalBalance.amount, 6)
    return {
      remain: createBigDecimal(0n),
      cost,
      assetBalance: createBigDecimal(0n),
      totalBalance: {
        amount: minus(totalBalance.amount, amount),
        value: minus(totalBalance.value, cost),
      },
    }
  }

  // balance.amount > amount の場合
  // - cost = totalBalance.value * amount / totalBalance.amount
  // - remain = 0
  // - balance.amount, balance.value から amount 引く
  // - used balance に amount, cost 追加
  if (greaterThan(assetBalance, amount)) {
    const cost = equals(totalBalance.amount, amount)
      ? totalBalance.value
      : divide(times(totalBalance.value, amount, 6), totalBalance.amount, 6)
    return {
      remain: createBigDecimal(0n),
      cost,
      assetBalance: minus(assetBalance, amount),
      totalBalance: {
        amount: minus(totalBalance.amount, amount),
        value: minus(totalBalance.value, cost),
      },
    }
  }

  // balance.amount < amount の場合
  // - cost = totalBalance.value * assetBalance / totalBalance.amount
  // - remain = amount - balance.amount
  // - balance.amount, balance.value 0 にして、空配列返す
  // - used balance に balance 追加
  const cost = equals(totalBalance.amount, assetBalance)
    ? totalBalance.value
    : divide(times(totalBalance.value, assetBalance, 6), totalBalance.amount, 6)
  return {
    remain: minus(amount, assetBalance),
    cost,
    assetBalance: createBigDecimal(0n),
    totalBalance: {
      amount: minus(totalBalance.amount, assetBalance),
      value: minus(totalBalance.value, cost),
    },
  }
}
